// Return-visit reminder for one service (admin): a date + note, plus the "gray out"
// flag — parking is set alongside the reminder and removing the reminder un-parks
// (v1's paired behavior, carried exactly).
import { useState } from 'preact/hooks';
import { setParked } from '../../state/admin';
import { returnFor } from '../../state/adminUi';
import { parked } from '../../state/completions';
import { clearReminder, reminders, setReminder } from '../../state/reminders';
import { toast } from '../../state/toast';
import { AdminSheet } from './AdminSheet';

export function ReturnSheet({
  target,
}: {
  target: { id: string; development: string; address: string };
}) {
  const close = () => (returnFor.value = null);
  const existing = reminders.value[target.id];
  const [date, setDate] = useState(existing?.date ?? '');
  const [note, setNote] = useState(existing?.note ?? '');
  const [dim, setDim] = useState(parked.value.has(target.id));
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!date) {
      toast('Pick a date for the reminder.');
      return;
    }
    setBusy(true);
    const ok = await setReminder(target.id, {
      date,
      note: note.trim(),
      address: target.address,
      development: target.development,
    });
    await setParked(target.id, dim);
    setBusy(false);
    if (ok) {
      toast('Reminder saved.');
      close();
    } else {
      toast('Could not save the reminder.');
    }
  }

  async function remove() {
    setBusy(true);
    const ok = await clearReminder(target.id);
    await setParked(target.id, false); // removing the reminder also un-grays (v1)
    setBusy(false);
    if (ok) {
      toast('Reminder removed.');
      close();
    }
  }

  return (
    <AdminSheet
      title="Return visit"
      sub={target.development || target.address || target.id}
      onClose={close}
      testId="return-sheet"
    >
      <label class="mt-1 block text-sm font-semibold text-navy-900" for="retDate">
        Remind on
      </label>
      <input
        id="retDate"
        type="date"
        class="tap mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
        value={date}
        onInput={(e) => setDate((e.target as HTMLInputElement).value)}
      />
      <label class="mt-3 block text-sm font-semibold text-navy-900" for="retNote">
        Note <span class="font-normal text-slate-400">(optional)</span>
      </label>
      <textarea
        id="retNote"
        rows={3}
        maxLength={600}
        class="tap mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
        placeholder="Parts on order, call before coming…"
        value={note}
        onInput={(e) => setNote((e.target as HTMLTextAreaElement).value)}
      />
      <label class="tap mt-3 flex items-center gap-2 py-1 font-semibold text-navy-900">
        <input
          type="checkbox"
          class="h-5 w-5 accent-brand-600"
          checked={dim}
          onChange={(e) => setDim((e.target as HTMLInputElement).checked)}
        />
        <span>
          <b>Gray out this service</b> — shows it on hold on everyone's board
        </span>
      </label>
      <div class="mt-4 flex gap-2">
        {existing && (
          <button class="btn-danger-outline" onClick={() => void remove()} disabled={busy}>
            Remove
          </button>
        )}
        <button class="btn-ghost flex-1" onClick={close} disabled={busy}>
          Cancel
        </button>
        <button
          class="btn-primary flex-1"
          data-testid="return-save"
          onClick={() => void save()}
          disabled={busy}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </AdminSheet>
  );
}
